import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaGamepad, FaUser, FaMobileAlt, FaEnvelope } from 'react-icons/fa';
import { useLang } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';

const categoryIcons = [<FaGamepad />, <FaUser />, <FaMobileAlt />, <FaEnvelope />];

const AccordionItem = ({ item, isOpen, onToggle }) => (
  <div className={`border rounded-xl overflow-hidden transition-all duration-200 ${isOpen ? 'border-headingColor/30 bg-white' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-6 py-5 text-left gap-4"
    >
      <span className={`font-bold text-sm leading-snug ${isOpen ? 'text-headingColor' : 'text-gray-800'}`}>{item.q}</span>
      <FaChevronDown className={`text-gray-400 text-xs shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-headingColor' : ''}`} />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        >
          <div className="px-6 pb-5 text-gray-500 text-sm leading-relaxed border-t border-gray-100 pt-4">
            {item.a}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FaqContainer = () => {
  const { lang } = useLang();
  const t = translations[lang].faq;
  const [openKey, setOpenKey] = useState(null);
  const toggle = (key) => setOpenKey(prev => prev === key ? null : key);

  const faqData = t.categories.map((cat, i) => ({
    ...cat,
    icon: categoryIcons[i] || <FaEnvelope />,
  }));

  return (
    <div className="w-full min-h-screen bg-gray-950">

      {/* ── Banner ── */}
      <div className="relative w-full bg-gray-950 pt-10 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute right-0 top-0 w-[500px] h-[400px] bg-headingColor/8 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="text-headingColor text-xs font-bold tracking-widest uppercase mb-3">{t.eyebrow}</p>
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight mb-4">{t.title}</h1>
          <p className="text-gray-500 text-sm max-w-md mx-auto">{t.subtitle}</p>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="w-full bg-gray-50 py-16 px-6">
        <div className="max-w-3xl mx-auto flex flex-col gap-14">
          {faqData.map((section, si) => (
            <motion.div
              key={si}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: si * 0.08 }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 bg-headingColor/10 rounded-xl flex items-center justify-center text-headingColor text-sm">
                  {section.icon}
                </div>
                <h2 className="text-xl font-black text-gray-900 tracking-tight">{section.name}</h2>
              </div>

              <div className="flex flex-col gap-2">
                {section.items.map((item, ii) => {
                  const key = `${si}-${ii}`;
                  return (
                    <AccordionItem
                      key={key}
                      item={item}
                      isOpen={openKey === key}
                      onToggle={() => toggle(key)}
                    />
                  );
                })}
              </div>
            </motion.div>
          ))}

          {/* Still have questions */}
          <motion.div
            className="bg-gray-950 rounded-2xl p-10 text-center relative overflow-hidden"
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[150px] bg-headingColor/12 rounded-full blur-[60px] pointer-events-none" />
            <div className="relative z-10">
              <FaEnvelope className="text-headingColor/50 text-3xl mx-auto mb-4" />
              <h3 className="text-2xl font-black text-white mb-2 tracking-tight">{t.stillTitle}</h3>
              <p className="text-gray-500 text-sm mb-6">{t.stillSubtitle}</p>
              <a
                href="mailto:info@harmanplay.com"
                className="inline-flex items-center gap-2 bg-headingColor hover:bg-red-700 text-white px-7 py-3 rounded-xl font-bold text-sm transition-all duration-200 hover:-translate-y-0.5 shadow-xl shadow-red-950/40"
              >
                <FaEnvelope size={12} /> {t.stillBtn}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FaqContainer;
